import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import useTranslations from "@/shared/lang/useTranslations";

import type { UserListItem } from "../types/users-api.types";

export interface SuspendUserDialogProps {
  user: UserListItem | null;
  isBusy: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

/**
 * Suspending a person.
 *
 * Confirmed, not one click inside a dropdown: suspension locks someone out of the platform entirely, and the
 * menu item sits directly above Delete in the same destructive group. Deactivate and Activate stay
 * unconfirmed — both are reversible in one click and neither denies access the way a suspension does.
 *
 * The copy states the asymmetry an operator has to know: the guard re-reads the user on every request, so a
 * suspension takes effect on the next call — but the session rows are NOT revoked (unlike delete), so the
 * person is refused rather than signed out.
 */
export function SuspendUserDialog({ user, isBusy, onConfirm, onOpenChange }: SuspendUserDialogProps) {
  const { t } = useTranslations("users");

  return (
    <ColocaAlertDialog
      open={user !== null}
      onOpenChange={onOpenChange}
      title={t("suspend_title", { name: user?.full_name ?? "" })}
      description={t("suspend_body")}
      confirmLabel={t("suspend_confirm")}
      confirmVariant="destructive"
      confirmDisabled={isBusy}
      cancelLabel={t("action_cancel")}
      onConfirm={onConfirm}
    />
  );
}
